import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { signin, authenticate, decodeJwtExp, hasValidAccessToken, AdminLoginResponse } from '../../logic/apis/auth'
import { scheduleProactiveRefresh, setAccessToken, setCsrfToken, resetAuthFlags, ApiError } from '../../logic/apis'

interface AuthState {
  user: {
    name: string
    profilePic: string
    email: string
    role: string
    id: string
  }
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

const readStoredJsonString = (key: string): string => {
  const raw = localStorage.getItem(key)
  if (!raw) return ''
  try {
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'string' ? parsed : ''
  } catch {
    return ''
  }
}

const initialState: AuthState = {
  user: {
    name: '',
    profilePic: '',
    email: '',
    role: readStoredJsonString('role'),
    id: readStoredJsonString('id'),
  },
  // Post-Wave 3C the access token lives in memory only, so a hard reload
  // always starts unauthenticated until bootstrapSession() resolves
  // (see App.tsx). For legacy sessions with a token still in localStorage,
  // hasValidAccessToken() will promote it to memory via getAccessToken() and
  // return true here so the UI doesn't flash unauthed.
  isAuthenticated: hasValidAccessToken(),
  loading: false,
  error: null,
}

export const loginAdmin = createAsyncThunk(
  'auth/login',
  async (data: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = (await signin(data)) as unknown as AdminLoginResponse
      const accessToken: string | undefined = response?.token
      const tokenExpireAt: number | undefined =
        response?.admin?.exp ?? (accessToken ? decodeJwtExp(accessToken) : undefined)

      if (!accessToken) {
        return rejectWithValue('Authentication failed')
      }

      // Access token lives in memory; refresh token is set as an httpOnly
      // cookie by the backend on /admin/login (see Wave 3A). We deliberately
      // do NOT persist the access token or refresh token to localStorage.
      setAccessToken(accessToken)
      // CSRF token rides in the response body because cross-domain admin SPAs
      // can't read the JS-readable csrf cookie via document.cookie. Optional —
      // older backends don't return it, in which case the in-memory slot stays
      // null and refresh falls back to readCsrfCookie().
      if (response?.csrf_token) {
        setCsrfToken(response.csrf_token)
      }

      const idPersisted = authenticate(tokenExpireAt, response?.admin?._id, response?.admin?.role)
      if (idPersisted) {
        // Clear the loggedOut race-guard so a fresh signin in the same tab
        // (after a previous explicit logout) can mint tokens cleanly.
        resetAuthFlags()
        // Arm the proactive refresh timer now that the session is live.
        scheduleProactiveRefresh()
        return {
          id: response?.admin?._id,
          role: response?.admin?.role,
          email: data.email,
        }
      }
      return rejectWithValue('Authentication failed')
    } catch (error) {
      const message = (error as { apiError?: ApiError })?.apiError?.message || 'Login failed'
      return rejectWithValue(message)
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateUser(state, action: PayloadAction<Partial<AuthState['user']>>) {
      state.user = { ...state.user, ...action.payload }
    },
    // Mark the session as restored after the silent bootstrap swap succeeds.
    // App.tsx dispatches this once bootstrapSession() resolves positive.
    restoreSession(state) {
      state.isAuthenticated = true
      state.user.role = readStoredJsonString('role')
      state.user.id = readStoredJsonString('id')
    },
    logout(state) {
      // Session-watcher teardown is the caller's responsibility (see Userbox).
      // We still wipe storage + in-memory token here so any stale render
      // reads an unauthed state. Server-side revocation (which also clears
      // the httpOnly refresh cookie) happens via revokeAdminSession() in
      // the caller before this reducer runs.
      setAccessToken(null)
      setCsrfToken(null)
      localStorage.clear()
      state.isAuthenticated = false
      state.user = { name: '', profilePic: '', email: '', role: '', id: '' }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user.id = action.payload.id
        state.user.role = action.payload.role
        state.user.email = action.payload.email
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { updateUser, restoreSession, logout } = authSlice.actions
export default authSlice.reducer
